// src/components/PatchNotes/PatchNotes.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Paper,
    Container,
    Chip,
    Stack,
    Skeleton,
    Alert,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    Close as CloseIcon,
    NewReleases as NewReleasesIcon,
    Build as BuildIcon,
    BugReport as BugReportIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { listPatchNotes } from '../../ctapi/patchNotesService';

const getIconForType = (type) => {
    switch (type) {
        case 'new':
            return <NewReleasesIcon sx={{ color: 'success.main', fontSize: '1.25rem' }} />;
        case 'improvement':
            return <BuildIcon sx={{ color: 'info.main', fontSize: '1.25rem' }} />;
        case 'fix':
            return <BugReportIcon sx={{ color: 'error.main', fontSize: '1.25rem' }} />;
        default:
            return null;
    }
};

const PatchNotesContent = ({ notes }) => {
  if (!notes || notes.length === 0) {
      return <Typography color="text.secondary" sx={{textAlign: 'center', py: 4}}>No updates to display for this category.</Typography>;
  }

  return (
    <Box>
      {notes.map((release) => (
        <Paper key={release.version} variant="outlined" sx={{ position: 'relative', p: { xs: 2, md: 3 }, mb: 4, borderRadius: 4, overflow: 'hidden' }}>
            <Box sx={{
                position: 'absolute',
                width: '4px',
                height: '100%',
                bgcolor: 'primary.main',
                left: 0,
                top: 0,
            }} />
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{release.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {release.date} &bull; Version {release.version} &bull; Category: {release.category}
                    </Typography>
                </Box>
                <Stack component="ul" sx={{ mt: 2, p: 0, listStyle: 'none' }} spacing={1.5}>
                    {release.notes.map((note, index) => (
                        <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                icon={getIconForType(note.type)}
                                label={note.type}
                                size="small"
                                variant="outlined"
                                color={
                                    note.type === 'new' ? 'success' :
                                    note.type === 'improvement' ? 'info' :
                                    'error'
                                }
                                sx={{ textTransform: 'capitalize', minWidth: '110px' }}
                            />
                            <Typography variant="body1">{note.description}</Typography>
                        </Box>
                    ))}
                </Stack>
            </Stack>
        </Paper>
      ))}
    </Box>
  );
};

export const PatchNotesModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { data, isLoading } = useQuery({ queryKey: ['patchNotes'], queryFn: listPatchNotes });
    const latestNotes = data?.results?.slice(0, 1) || [];

    const handleViewAll = () => {
        onClose();
        navigate('/patch-notes');
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px', maxHeight: '90vh' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    What's New
                </Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                {isLoading ? <Skeleton variant="rectangular" height={150} /> : <PatchNotesContent notes={latestNotes} />}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleViewAll} sx={{ textTransform: 'none' }}>
                    View all updates
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const PatchNotesPage = () => {
    const { data, isLoading, isError } = useQuery({ queryKey: ['patchNotes'], queryFn: listPatchNotes });
    const [selectedCategory, setSelectedCategory] = useState('all');

    const handleCategoryChange = (event, newCategory) => {
        if (newCategory !== null) {
            setSelectedCategory(newCategory);
        }
    };

    const filteredNotes = useMemo(() => {
        if (!data) return [];
        if (selectedCategory === 'all') return data.results;
        return data.results.filter(note => note.category.toLowerCase() === selectedCategory);
    }, [data, selectedCategory]);

    const categories = useMemo(() => {
        if (!data) return [];
        const allCategories = data.results.map(note => note.category);
        return ['all', ...Array.from(new Set(allCategories))];
    }, [data]);

    return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 400 }}>Release Notes</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Here's a timeline of all the awesome things we've been working on.
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <ToggleButtonGroup
                value={selectedCategory}
                exclusive
                onChange={handleCategoryChange}
                aria-label="patch notes category"
            >
                {categories.map(category => (
                    <ToggleButton key={category} value={category.toLowerCase()} aria-label={`${category} view`} sx={{textTransform: 'capitalize'}}>
                        {category}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>

        {isLoading && (
            <Stack spacing={4}>
                <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 4 }}/>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 4 }}/>
            </Stack>
        )}
        {isError && <Alert severity="error">Could not load release notes. Please try again later.</Alert>}
        {data && <PatchNotesContent notes={filteredNotes} />}
    </Container>
  );
};